/** @jsxImportSource @emotion/react */
import { Header } from '@quickbit/backoffice-core-frontend';
import {
  Typography,
  useForm,
  useTheme,
  DateInput,
  Pagination,
  UtilityButton,
  Icon,
  Option,
  Card,
  UtilitySelect,
  Tooltip,
} from '@quickbit/qb-design-system';
import { capitalize } from 'lodash';
import { useEffect, useState } from 'react';
import { useSessionStorage } from 'react-use';
import {
  PagoraQuickbitFilters,
  TransactionType,
  useHotwalletBalances,
  usePagoraQuickbitOverview,
} from 'api/useDirectOnrampApi';
import { QBLoader } from 'components';
import { CustodianTransactionTable } from '../components';

type FilterStore = {
  filters: PagoraQuickbitFilters;
  pagination: {
    page: number;
    size: number;
  };
} | null;

const PAGE_SIZE = 16;

const DEFAULT_CREATED_FROM = new Date();
DEFAULT_CREATED_FROM.setDate(DEFAULT_CREATED_FROM.getDate() - 3);

const USER_FILTER_OPTIONS = [
  { label: 'Pagora', value: 'pagora' },
  { label: 'Quickbit', value: 'quickbit' },
];

const TYPE_OPTIONS = Object.keys(TransactionType).map((key) => ({
  value: key,
  label: TransactionType[key as keyof typeof TransactionType],
}));

const buildDefaultFilterFormValues = (store: FilterStore) =>
  ({
    createdAtGt: store?.filters.createdAtGt
      ? new Date(store.filters.createdAtGt)
      : DEFAULT_CREATED_FROM,
    createdAtLt: store?.filters.createdAtLt
      ? new Date(store.filters.createdAtLt)
      : undefined,
    user: store?.filters.user || undefined,
    types: store?.filters.types || undefined,
  } as PagoraQuickbitFilters);

export const PagoraQuickbitDashboard = () => {
  const { spacing, palette } = useTheme();

  const [filterStore, setFilterStore] = useSessionStorage<FilterStore>(
    'qbDirectOnrampPagoraQbDashboardFilterStore'
  );

  const [page, setPage] = useState(filterStore?.pagination.page || 1);

  const { fields, setters } = useForm<PagoraQuickbitFilters>(
    buildDefaultFilterFormValues(filterStore),
    {}
  );

  useEffect(() => {
    setFilterStore({
      filters: fields,
      pagination: { page, size: PAGE_SIZE },
    });
  }, [fields, page, setFilterStore]);

  const handleResetFilters = () => {
    setPage(1);
    setters.set({
      createdAtGt: DEFAULT_CREATED_FROM,
      createdAtLt: undefined,
      types: undefined,
      user: undefined,
    });
  };

  const { data, isFetching, refetch } = usePagoraQuickbitOverview(fields, {
    page,
    size: PAGE_SIZE,
  });

  const { data: hotWalletBalances } = useHotwalletBalances();

  if (!data) return <QBLoader />;

  return (
    <>
      <Header>
        <Typography variant="subtitle">Transaction Requests</Typography>
      </Header>
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: `auto 1fr repeat(${
            hotWalletBalances ? 2 : 0
          }, auto)`,
          gap: spacing.xxxs,
        }}
      >
        <Card css={{ alignSelf: 'end' }}>
          <Typography variant="subtitle">Aggregated</Typography>
          <Typography variant="bodySmallBold">
            <div
              css={{
                display: 'flex',
                flexDirection: 'column',
                gap: spacing.xxxs,
                paddingTop: spacing.xxxs,
              }}
            >
              <span
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.xxxs,
                }}
              >
                <Icon name="ALGO" /> {data.aggregatedData.totalCrypto}
              </span>
              <span
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.xxxs,
                }}
              >
                <Icon name="EUR" /> {data.aggregatedData.totalEuro}
              </span>
            </div>
          </Typography>
        </Card>
        <div />
        {hotWalletBalances && (
          <>
            <Card>
              <div
                css={{
                  display: 'flex',
                  gap: spacing.xxxs,
                  alignItems: 'center',
                  marginBottom: spacing.xxxs,
                }}
              >
                <Typography variant="subtitle">Current Balances</Typography>
                <Icon name="ALGO" />
              </div>
              <Typography variant="bodySmallBold">
                <div
                  css={{
                    display: 'flex',
                    gap: spacing.xxxs,
                    flexDirection: 'column',
                  }}
                >
                  <span>Pagora: {hotWalletBalances.currentPagoraBalance}</span>
                  <span>
                    Quickbit: {hotWalletBalances.currentQuickbitBalance}
                  </span>
                </div>
              </Typography>
            </Card>
            <Card>
              <div
                css={{
                  display: 'flex',
                  gap: spacing.xxxs,
                  alignItems: 'center',
                  marginBottom: spacing.xxxs,
                }}
              >
                <Typography variant="subtitle">Hotwallet Balances</Typography>
                <Icon name="ALGO" />
              </div>
              <Typography variant="bodySmallBold">
                <div
                  css={{
                    display: 'grid',
                    gap: spacing.xxs,
                    paddingTop: spacing.xxxs,
                    gridTemplateColumns: 'repeat(2, auto)',
                  }}
                >
                  <span>DCS: {hotWalletBalances.dcsHotwalletBalance}</span>
                  <span>Nexus: {hotWalletBalances.nexusBalances.total}</span>
                  <Tooltip title="Available, Locked, Unconfirmed">
                    <span>Nexus A, L, U:</span>
                  </Tooltip>
                  <Tooltip title="Available, Locked, Unconfirmed">
                    <span>
                      {hotWalletBalances.nexusBalances.available},{' '}
                      {hotWalletBalances.nexusBalances.locked},{' '}
                      {hotWalletBalances.nexusBalances.unconfirmed}
                    </span>
                  </Tooltip>
                </div>
              </Typography>
            </Card>
          </>
        )}
      </div>
      <div
        css={{
          marginTop: spacing.xxs,
          display: 'grid',
          gridTemplateColumns: '8fr 1fr',
          textWrap: 'nowrap',
        }}
      >
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing.xxxs,
            flexWrap: 'wrap',
          }}
        >
          <DateInput
            placeholder="All time"
            type="range"
            value={[
              fields.createdAtGt ? new Date(fields.createdAtGt) : null,
              fields.createdAtLt ? new Date(fields.createdAtLt) : null,
            ]}
            onChange={(e: [Date | null, Date | null]) => {
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              setters.createdAtGt?.set(e[0]!);

              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              setters.createdAtLt?.set(e[1]!);
            }}
            withTime
          />
          <UtilitySelect
            label={fields.user ? capitalize(fields.user) : 'Both users'}
            listTitle="User"
            options={USER_FILTER_OPTIONS}
            value={
              fields.user
                ? USER_FILTER_OPTIONS.find(
                    (userOption) => userOption.value === fields.user
                  ) || null
                : null
            }
            onSelect={(data) => {
              setPage(1);
              setters.user?.set(
                (data as Option | undefined)
                  ?.value as PagoraQuickbitFilters['user']
              );
            }}
          />
          <UtilitySelect
            label={
              fields.types && fields.types.length > 0
                ? `Types (${fields.types.length})`
                : 'All types'
            }
            listTitle="Type"
            options={TYPE_OPTIONS}
            value={
              fields.types?.map(
                (type) =>
                  TYPE_OPTIONS.find(
                    (typeOption) => typeOption.value === type
                  ) as Option
              ) ?? []
            }
            multiple
            onSelect={(data) => {
              setPage(1);
              setters.types?.set(
                data ? (data as Option[]).map((option) => option.value) : []
              );
            }}
          />
          <UtilityButton
            variant="text"
            text="Reset filters"
            onClick={handleResetFilters}
          />
          <UtilityButton
            variant="secondary"
            text="Refresh"
            onClick={() => refetch()}
            disabled={isFetching}
          />
          {isFetching && (
            <Icon
              name="spinner"
              className={{ svg: { color: palette.grey.default } }}
            />
          )}
        </div>
      </div>
      <div css={{ marginTop: spacing.xxs }}>
        <CustodianTransactionTable transactions={data.transactions} />
      </div>
      <div css={{ marginTop: spacing.xxs }}>
        <Pagination
          currentPage={page}
          onChange={setPage}
          numberOfElements={data.aggregatedData.count}
          pageSize={PAGE_SIZE}
        />
      </div>
    </>
  );
};
